/**
 * "I have seen this one" — per item, for the current browser session
 * (`useSessionDismissal`, a plan-change proposal) or for good in this browser
 * (`useBrowserDismissal`, a one-time tip).
 *
 * The home of every remembered dismissal, so none of them keeps its own
 * read/write pair.
 *
 * The key must identify the ITEM, not its contents: a key derived from what
 * the notice SAID lets dismissing one notice silence the next one about a
 * different thing. Callers build `<namespace>:<id>`, plus the item's state
 * when a later state of the same item is new news. A notice about one store's
 * data names the store in its key, or dismissing it on one store silences it
 * on every other store opened in the browser.
 */

import { useCallback, useState } from 'react';

/**
 * @param {string|null} key  storage key, or null when there is nothing to
 *   dismiss (a caller may run this before its own render guards, as a
 *   hook must)
 * @returns {[boolean, Function]} `[dismissed, dismiss]`
 */
export function useSessionDismissal(key) {
  return useDismissal('sessionStorage', key);
}

/** As `useSessionDismissal`, kept after the browser closes. */
export function useBrowserDismissal(key) {
  return useDismissal('localStorage', key);
}

function useDismissal(storage, key) {
  // The storage is the truth and is read per render (cheap); this counter
  // exists only to re-render after a dismiss click.
  const [, bump] = useState(0);

  const dismiss = useCallback(() => {
    if (!key) return;
    writeDismissed(storage, key);
    bump((n) => n + 1);
  }, [storage, key]);

  return [key ? readDismissed(storage, key) : false, dismiss];
}

// The one catch in this module, and the narrow case CLAUDE.md allows one
// for: storage can be disabled outright (Safari private mode, an iframe
// storage policy), the value is a UI preference rather than merchant data,
// and the failure direction is a notice that stays visible — never a
// number, a price, or a plan that reads wrong.
function readDismissed(storage, key) {
  if (typeof window === 'undefined') return false;
  try {
    return window[storage].getItem(key) === '1';
  } catch {
    // Silent by design HERE only: this runs on every render, so logging
    // it would spam the console for as long as the notice is on screen.
    // The write path below is the one that logs — it runs once per click
    // and is where an unavailable store first becomes observable.
    return false;
  }
}

function writeDismissed(storage, key) {
  if (typeof window === 'undefined') return;
  try {
    window[storage].setItem(key, '1');
  } catch (err) {
    console.warn(`noticeDismissal: not persisted — ${storage} unavailable`, err);
  }
}
